import { WarningCircleIcon } from '@phosphor-icons/react'
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react'
import { cx } from './cx.ts'

interface FieldShellProps {
  id: string
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  className?: string
  children: ReactNode
  hideLabel?: boolean
}

/** Label + control + hint/error, wired with ids so screen readers announce all three. */
export function FieldShell({ id, label, hint, error, required, className, children, hideLabel }: FieldShellProps) {
  return (
    <div className={cx('rt-field', className)}>
      {label ? (
        <label htmlFor={id} className={cx('rt-field__label', hideLabel && 'rt-visually-hidden')} data-required={required ? '' : undefined}>
          {label}
        </label>
      ) : null}
      {children}
      {error ? (
        <p id={`${id}-error`} className="rt-field__error" role="alert">
          <WarningCircleIcon weight="bold" aria-hidden />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="rt-field__hint">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  size?: 'md' | 'lg' | 'xl'
  prefix?: ReactNode
  suffix?: ReactNode
  mono?: boolean
  invalid?: boolean
  wrapperClassName?: string
}

/** A bare input with optional adornments (for filter bars). Use TextField in forms. */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', prefix, suffix, mono, invalid, wrapperClassName, disabled, className, ...rest },
  ref,
) {
  return (
    <div
      className={cx('rt-input', wrapperClassName)}
      data-size={size}
      data-mono={mono ? '' : undefined}
      data-invalid={invalid ? '' : undefined}
      data-disabled={disabled ? '' : undefined}
    >
      {prefix ? <span className="rt-input__adorn">{prefix}</span> : null}
      <input ref={ref} disabled={disabled} aria-invalid={invalid || undefined} className={className} {...rest} />
      {suffix ? <span className="rt-input__adorn">{suffix}</span> : null}
    </div>
  )
})

export interface TextFieldProps extends InputProps {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  hideLabel?: boolean
  fieldClassName?: string
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, hideLabel, id, required, fieldClassName, ...rest },
  ref,
) {
  const auto = useId()
  const fid = id ?? auto
  const describedBy = error ? `${fid}-error` : hint ? `${fid}-hint` : undefined
  return (
    <FieldShell id={fid} label={label} hint={hint} error={error} required={required} hideLabel={hideLabel} className={fieldClassName}>
      <Input ref={ref} id={fid} required={required} invalid={!!error} aria-describedby={describedBy} {...rest} />
    </FieldShell>
  )
})

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  fieldClassName?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, id, required, className, fieldClassName, ...rest },
  ref,
) {
  const auto = useId()
  const fid = id ?? auto
  const describedBy = error ? `${fid}-error` : hint ? `${fid}-hint` : undefined
  return (
    <FieldShell id={fid} label={label} hint={hint} error={error} required={required} className={fieldClassName}>
      <textarea
        ref={ref}
        id={fid}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cx('rt-textarea', className)}
        {...rest}
      />
    </FieldShell>
  )
})
